import { useRef, useState } from 'react';
import Link from 'next/link';
import cn from 'classnames';
// Hooks
import {
  useGetUnreadNotificationsCount,
  useOutsideClick,
  useGetNotifications,
  useNotificationList,
} from 'hooks';
// Constants
import { NOTIFICATIONS } from 'constants/routes';
// Components
import NotificationIconWithNumber from './NotificationIconWithNumber/NotificationIconWithNumber';
import SingleNotification from 'components/Notifications/components/DisplayNotifications/SingleNotification/SingleNotification';
import MultipleNotificationsActions from 'components/Notifications/components/MultipleNotificationsActions/MultipleNotificationsActions';
// Styles
import styles from './NotificationsButton.module.scss';

const NotificationsButton = () => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);

  // Only fetch count (lightweight) - has subscription for real-time updates
  const { data: unreadCountData } = useGetUnreadNotificationsCount();

  // Only fetch notifications when dropdown is opened
  const { notifications: notificationsData, onUpdate } = useNotificationList({
    useHook: () => useGetNotifications({ skipApi: !isDropdownVisible }),
    isButton: true, // this flag limits the number of notifications to 8
  });

  const unreadCount = Number(unreadCountData?.getUnreadNotificationsCount);

  const notifications = notificationsData || [];

  useOutsideClick(dropdownRef, () => {
    setIsDropdownVisible(false);
  });

  const toggleDropdown = () => {
    setIsDropdownVisible((prev) => !prev);
  };

  const handleCloseDropdown = () => {
    setIsDropdownVisible(false);
  };

  return (
    <div className={styles.root} ref={dropdownRef}>
      <button type="button" className={styles.button} onClick={toggleDropdown}>
        <NotificationIconWithNumber hasUnread={!!unreadCount} />
      </button>
      {isDropdownVisible && (
        <div className={styles.dropdown}>
          <div className={styles.headingWrapper}>
            <p className={styles.heading}>Notifications</p>
            <MultipleNotificationsActions isSmall onUpdate={onUpdate} />
          </div>

          <div className={styles.notificationsListWrapper}>
            <ul
              aria-hidden={!isDropdownVisible}
              className={cn(styles.notificationsDropdown, {
                [styles.visible]: isDropdownVisible,
              })}
            >
              {notifications.map((item) => (
                <li key={item.id}>
                  <SingleNotification
                    notification={item}
                    isDropdown={true}
                    onCloseDropdown={handleCloseDropdown}
                  />
                </li>
              ))}
            </ul>
          </div>

          <Link href={NOTIFICATIONS} passHref legacyBehavior>
            <a className={styles.seeAllButton}>See all notifications</a>
          </Link>
        </div>
      )}
    </div>
  );
};

export default NotificationsButton;
